// cSpell:ignore Aanvulling afgerekend Afrekening choosable deze Eindafrekening geen Keuze kosten Naam Periode periode Samenvatting unticked vertrek Verwerking Volgende voor zijn
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getSettlementTitle } from "@rentiohq/shared-frontend/dist/utils/settlement.utils";
import { awaitsYearlyReadings } from "../../../departure.utils";
import { subtractIntervals } from "../../../engine";
import {
  ContractRef,
  DistributionMethod,
  Invoice,
  isOwnerOnlyCategory,
  PerContractResult,
  settlementEligible,
  SettlementDraftState,
  settlementIneligibleReason,
} from "../../../types";
import { overlapsPeriod, withinPeriod } from "./steps/dates";

// Below these a tenant line or the owner part is rounding noise, the same
// thresholds the Samenvatting uses to decide what it shows.
const TENANT_LINE_THRESHOLD = 0.001;
const OWNER_TOTAL_THRESHOLD = 0.01;

const ID_KEY_SEPARATOR = ",";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const CENTS_PER_EURO = 100;
// Two percentages (VAT, tenant share) are the same below half a hundredth.
const PERCENTAGE_TOLERANCE = 0.005;

// A content key for a set of ids: the same ids give the same key, in whatever
// order or container they arrive. The settlement and cost lists come out of
// the store as new arrays on every render, so a Set built from them is new on
// every render too; keying a memo on this string keeps its identity stable.
export const getIdSetKey = (ids: Iterable<string>): string =>
  [...new Set(ids)].sort().join(ID_KEY_SEPARATOR);

export const getIdSetFromKey = (key: string): Set<string> =>
  new Set(key ? key.split(ID_KEY_SEPARATOR) : []);

// The costs a new settlement starts with: every cost that can be settled here
// and lies wholly within the period (decision 1, 2026-10-20); a cost that
// runs past it is offered unticked, with a warning (getCostsPastPeriod). A
// departure settlement takes every cost that overlaps its period (its
// tenant's share up to the lease end), and leaves out the costs that wait for
// the readings of the year (see awaitsYearlyReadings).
export const getAutoSelectedInvoiceIds = (params: {
  invoices: Invoice[];
  excludedInvoiceIds: Set<string>;
  period: { from: string; to: string };
  departure?: { methodByCategory?: Record<string, DistributionMethod> };
}): Record<string, boolean> => {
  const { invoices, excludedInvoiceIds, period, departure } = params;
  const selected: Record<string, boolean> = {};
  for (const invoice of invoices) {
    if (
      settlementEligible(invoice) &&
      !excludedInvoiceIds.has(invoice.id) &&
      (departure
        ? overlapsPeriod(invoice, period)
        : withinPeriod(invoice, period)) &&
      !(
        departure &&
        awaitsYearlyReadings({
          invoice,
          methodByCategory: departure.methodByCategory,
        })
      )
    ) {
      selected[invoice.id] = true;
    }
  }
  return selected;
};

// The draft with the costs of its period preselected, for a draft that has no
// selection yet. It returns the very same draft whenever there is nothing to
// change, so a setDraft with it is a no-op: a new object for an empty
// selection re-rendered the wizard endlessly, and the broker's clicks were
// lost in between.
export const withAutoSelectedInvoices = (params: {
  draft: SettlementDraftState | null;
  invoices: Invoice[];
  excludedInvoiceIds: Set<string>;
}): SettlementDraftState | null => {
  const { draft, invoices, excludedInvoiceIds } = params;
  if (!draft || Object.keys(draft.selectedInvoiceIds).length > 0) return draft;
  const selectedInvoiceIds = getAutoSelectedInvoiceIds({
    invoices,
    excludedInvoiceIds,
    period: draft.period,
    departure: draft.departure
      ? { methodByCategory: draft.distributionMethod }
      : undefined,
  });
  if (Object.keys(selectedInvoiceIds).length === 0) return draft;
  return { ...draft, selectedInvoiceIds };
};

// A departure draft saved before its lease end moved (M11): "Tot" is locked
// on the lease's current end (N28) and the API refuses a departure that ends
// elsewhere, so the draft follows the new end. Its start stays, unless it now
// lies after that end (then `defaultFrom`, see departureDefaultStart), and
// its costs are ticked again for the new period (withAutoSelectedInvoices).
// The very same draft when the end did not move.
export const withDepartureLeaseEnd = (params: {
  draft: SettlementDraftState;
  leaseEnd: string;
  defaultFrom: string;
}): SettlementDraftState => {
  const { draft, defaultFrom } = params;
  const leaseEnd = params.leaseEnd.slice(0, 10);
  if (!draft.departure || draft.departure.until.slice(0, 10) === leaseEnd) {
    return draft;
  }
  const from =
    draft.period.from && draft.period.from <= leaseEnd
      ? draft.period.from
      : defaultFrom;
  return {
    ...draft,
    departure: { ...draft.departure, until: leaseEnd },
    period: { from, to: leaseEnd },
    selectedInvoiceIds: {},
  };
};

// A cost (partly) charged to the tenant: only those decide which provision
// months a settlement uses, the same rule as the engine.
export const isTenantCost = (invoice: Invoice): boolean =>
  invoice.target !== "owner" && !isOwnerOnlyCategory(invoice.category);

export interface IOpenCostsWarning {
  textKey: string;
  count: number;
}

// The warning for open tenant costs of the period that the broker leaves out.
// The provision months follow the selected tenant costs and each month is
// settled once, so a later settlement of the left-out costs deducts no
// provision for them. With no tenant cost selected at all, the settlement
// uses up every provision month of its period, which gets its own, stronger
// wording.
export const getOpenCostsWarning = (params: {
  invoices: Invoice[];
  selectedInvoiceIds: Record<string, boolean>;
  excludedInvoiceIds: Set<string>;
  period: { from: string; to: string };
}): IOpenCostsWarning | undefined => {
  const { invoices, selectedInvoiceIds, excludedInvoiceIds, period } = params;
  const openTenantCosts = invoices.filter(
    invoice => !excludedInvoiceIds.has(invoice.id) && isTenantCost(invoice),
  );
  // A cost that runs past the period has a warning of its own
  // (getCostsPastPeriod): it does not belong to this period alone.
  const leftOut = openTenantCosts.filter(
    invoice => !selectedInvoiceIds[invoice.id] && withinPeriod(invoice, period),
  );
  if (leftOut.length === 0) return undefined;
  const isOne = leftOut.length === 1;
  const anyTenantCostSelected = openTenantCosts.some(
    invoice => selectedInvoiceIds[invoice.id],
  );
  if (!anyTenantCostSelected) {
    return {
      textKey: isOne
        ? "settlement.invoices.open_costs_warning_none_selected_one"
        : "settlement.invoices.open_costs_warning_none_selected_many",
      count: leftOut.length,
    };
  }
  return {
    textKey: isOne
      ? "settlement.invoices.open_costs_warning_one"
      : "settlement.invoices.open_costs_warning_many",
    count: leftOut.length,
  };
};

// The open costs that overlap the period without lying wholly within it
// (decision 1, 2026-10-20): offered unticked, and the step says so. Taken
// along, such a cost counts whole, split over its own period; a lease that
// starts only after the period pays none of it (the engine's
// startsAfterPeriod). None for a departure, whose period ends with its lease.
// Only the ones still unticked (M38): the warning says they are not ticked,
// so it counts down as the broker ticks them, and goes once all are.
export const getCostsPastPeriod = (params: {
  invoices: Invoice[];
  excludedInvoiceIds: Set<string>;
  period: { from: string; to: string };
  isDeparture?: boolean;
  selectedInvoiceIds?: Record<string, boolean>;
}): Invoice[] => {
  const { invoices, excludedInvoiceIds, period, isDeparture } = params;
  if (isDeparture) return [];
  return invoices.filter(
    invoice =>
      settlementEligible(invoice) &&
      !excludedInvoiceIds.has(invoice.id) &&
      !params.selectedInvoiceIds?.[invoice.id] &&
      overlapsPeriod(invoice, period) &&
      !withinPeriod(invoice, period),
  );
};

// The open costs of a period confirmed settlements already settled that
// came in later (decision 11, 2026-10-20): wholly before this settlement's
// period, and within the periods of earlier settlements of the tree, one or
// several together. A yearly cost of a year settled in two halves falls in
// neither half alone, and is late once both are confirmed (decision 4 of
// 2026-10-20, X10). They may be taken into this settlement, where they are
// offered: split over their own period, with no provision of their months
// against them (those months were settled). With the names of the
// settlements they are late for: those whose period meets the cost's,
// oldest first.
//
// Only a settlement whose scope took such a cost makes it late (M16): a
// building cost against a building settlement, a unit's own cost against a
// building settlement or a settlement of that unit. An interim settlement of
// one unit settles its own costs and its private provision only, so a
// building cost of its year stays an open cost of a year whose common
// provision is not settled yet.
export interface ISettledPeriod {
  name: string;
  from: string;
  to: string;
  // The settlement's scope and, for a unit settlement, its unit. Without
  // them a period covers every cost.
  scope?: "group" | "unit";
  unitId?: string;
}

const settledPeriodCovers = (
  settledPeriod: ISettledPeriod,
  invoice: Invoice,
): boolean => {
  if (!settledPeriod.scope || settledPeriod.scope === "group") return true;
  if (invoice.scope === "common") return false;
  return String(settledPeriod.unitId) === String(invoice.unitId);
};

export const getLateCosts = (params: {
  invoices: Invoice[];
  excludedInvoiceIds: Set<string>;
  period: { from: string; to: string };
  settledPeriods: ISettledPeriod[];
}): { invoices: Invoice[]; settlementNames: string[] } => {
  const { invoices, excludedInvoiceIds, period, settledPeriods } = params;
  // Oldest first, as the banner names them.
  const settledAll = settledPeriods
    .map(settledPeriod => ({
      ...settledPeriod,
      from: settledPeriod.from.slice(0, 10),
      to: settledPeriod.to.slice(0, 10),
    }))
    .sort((a, b) => a.from.localeCompare(b.from));
  const names = new Set<string>();
  const late = invoices.filter(invoice => {
    if (!settlementEligible(invoice) || excludedInvoiceIds.has(invoice.id)) {
      return false;
    }
    const from = invoice.periodFrom.slice(0, 10);
    const to = invoice.periodTo.slice(0, 10);
    if (to >= period.from) return false;
    const settledIn = settledAll.filter(
      settledPeriod =>
        settledPeriod.from <= to &&
        settledPeriod.to >= from &&
        settledPeriodCovers(settledPeriod, invoice),
    );
    if (subtractIntervals([{ from, to }], settledIn).length > 0) return false;
    settledIn.forEach(settledPeriod => names.add(settledPeriod.name));
    return true;
  });
  return { invoices: late, settlementNames: [...names] };
};

// The intro of a unit's "Keuze van kosten" (M43): a departure ticks its
// tenant's share of every cost up to the lease end, not only the costs
// wholly within the period, and says so.
export const getUnitInvoicesIntro = (
  departureLease?: Pick<ContractRef, "tenantName" | "endDate">,
): { key: string; until?: string; tenant?: string } =>
  departureLease
    ? {
        key: "settlement.invoices.departure_intro",
        tenant: departureLease.tenantName,
        until: departureLease.endDate,
      }
    : { key: "settlement.invoices.unit_intro" };

// What a settlement without costs says (M44): that the tenants get their
// provision back, or for a departure, which settles one tenant, that tenant
// by name. On "Keuze van kosten" (its checkbox) and on the summary.
export const getNoCostsText = (params: {
  on: "choice" | "summary";
  departureTenant?: string;
}): string => {
  const { on, departureTenant } = params;
  if (departureTenant) {
    return getLocalizedText(
      on === "choice"
        ? "settlement.invoices.no_costs_confirm_departure"
        : "settlement.summary.no_costs_departure",
      { tenant: departureTenant },
    );
  }
  return getLocalizedText(
    on === "choice"
      ? "settlement.invoices.no_costs_confirm"
      : "settlement.summary.no_costs",
  );
};

// The cost choice's two lists (M24): the costs that can still be chosen,
// and under "Al afgerekend (n)" the settled ones of this period. A settled
// cost of another period says nothing about this one, so it is left out:
// listed, it made "Alle kosten van deze periode zijn al afgerekend" appear
// for a period that had no cost at all.
export const splitSettledCosts = (params: {
  invoices: Invoice[];
  isSettled: (invoice: Invoice) => boolean;
  period: { from: string; to: string };
}): { choosable: Invoice[]; settledInPeriod: Invoice[] } => {
  const { invoices, isSettled, period } = params;
  return {
    choosable: invoices.filter(invoice => !isSettled(invoice)),
    settledInPeriod: invoices.filter(
      invoice =>
        isSettled(invoice) &&
        invoice.periodFrom.slice(0, 10) <= period.to &&
        invoice.periodTo.slice(0, 10) >= period.from,
    ),
  };
};

// A departure from a unit in a building whose period has no building
// (common) cost entered yet (decision 4, 2026-10-20): the tenant gets the
// common provision of those months back in full, and the building's costs
// that come in later are settled with them afterwards. Bea is warned, not
// stopped. Checked per calendar year of the period (M13), as the building
// settles per year: a 2025 cost says nothing of 2026. Only a cost charged
// at least partly to the tenants counts (isTenantCost): an owner's cost
// never stands against the provision. A lease whose common costs are a
// fixed amount has no common provision to give back. The years without
// such a cost, oldest first; none when every year has one.
export const yearsWithoutCommonCost = (params: {
  invoices: Invoice[];
  period: { from: string; to: string };
  // The departing lease prices its common costs as a fixed amount.
  isCommonFixed?: boolean;
}): string[] => {
  const { invoices, period, isCommonFixed } = params;
  if (isCommonFixed || !period.from || !period.to) return [];
  const commonTenantCosts = invoices.filter(
    invoice => invoice.scope === "common" && isTenantCost(invoice),
  );
  const missing: string[] = [];
  const lastYear = Number(period.to.slice(0, 4));
  for (let year = Number(period.from.slice(0, 4)); year <= lastYear; year++) {
    const from = [period.from, `${year}-01-01`].sort()[1];
    const to = [period.to, `${year}-12-31`].sort()[0];
    const hasCost = commonTenantCosts.some(
      invoice =>
        invoice.periodFrom.slice(0, 10) <= to &&
        invoice.periodTo.slice(0, 10) >= from,
    );
    if (!hasCost) missing.push(String(year));
  }
  return missing;
};

export interface IPeriodStepErrors {
  name?: string;
  from?: string;
  to?: string;
}

// The longest name the period step takes.
export const SETTLEMENT_NAME_MAX_LENGTH = 100;

// The name the wizard suggests for a settlement: its kind and period
// ("Afrekening 2026", "Afrekening 01/01–30/06/2026", "Eindafrekening bij
// vertrek"; getSettlementTitle), in the broker's language. A departure's
// suggestion names no tenant: the name reaches the tenant (Q2).
// A period that overlaps a confirmed settlement (M22) is suggested as
// "Aanvulling 2026" (or "Aanvulling 01/01–30/06/2026"): "Afrekening 2026"
// repeated the name of the one already there.
export const getSuggestedSettlementName = (
  draft: Pick<SettlementDraftState, "departure" | "period" | "isSupplementary">,
): string => {
  if (!draft.departure && draft.isSupplementary) {
    const label = periodNameLabel(draft.period);
    if (label) {
      return getLocalizedText(
        "year" in label
          ? "settlement.period.supplementary_name_year"
          : "settlement.period.supplementary_name_period",
        label,
      );
    }
  }
  return getSettlementTitle({
    kind: draft.departure ? "departure" : "regular",
    from: draft.period.from,
    to: draft.period.to,
  });
};

// The period as a settlement's name prints it (as getSettlementTitle does):
// the year for a calendar year, the year once within one year, both dates
// in full across years. Undefined without a valid period.
export const periodNameLabel = (period: {
  from: string;
  to: string;
}): { year: string } | { from: string; to: string } | undefined => {
  const from = period.from?.slice(0, 10) ?? "";
  const to = period.to?.slice(0, 10) ?? "";
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to)) return undefined;
  const [fromYear, fromMonth, fromDay] = from.split("-");
  const [toYear, toMonth, toDay] = to.split("-");
  if (
    fromYear === toYear &&
    `${fromMonth}-${fromDay}` === "01-01" &&
    `${toMonth}-${toDay}` === "12-31"
  ) {
    return { year: fromYear };
  }
  return {
    from:
      fromYear === toYear && from <= to
        ? `${fromDay}/${fromMonth}`
        : `${fromDay}/${fromMonth}/${fromYear}`,
    to: `${toDay}/${toMonth}/${toYear}`,
  };
};

// The confirmed settlements of the tree (see ISettledPeriod) whose period
// overlaps `period`, by name (M22): the period step says a new settlement
// of that period only takes what is still open.
export const overlappingSettlementNames = (params: {
  period: { from: string; to: string };
  settledPeriods: ISettledPeriod[];
}): string[] => {
  const { period, settledPeriods } = params;
  if (!period.from || !period.to) return [];
  return [
    ...new Set(
      settledPeriods
        .filter(
          settled =>
            settled.from.slice(0, 10) <= period.to &&
            settled.to.slice(0, 10) >= period.from,
        )
        .map(settled => settled.name),
    ),
  ];
};

// The period a new settlement (not a departure) proposes, once its costs
// and the confirmed settlements of the tree are known (M22). Last year,
// unless it is settled already or has no open cost while another year has:
// - the year of the oldest open cost whose year is not wholly settled yet;
// - else the first year after the settled ones, at the latest this year;
// - else last year.
// An open cost of a settled year is a late cost (decision 11): it goes into
// the next year's settlement, so it does not pull the proposal back.
export const proposeSettlementPeriod = (params: {
  invoices: Invoice[];
  excludedInvoiceIds: Set<string>;
  settledPeriods: ISettledPeriod[];
  today: string;
}): { from: string; to: string } => {
  const { invoices, excludedInvoiceIds, settledPeriods, today } = params;
  const yearOf = (year: number) => ({
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  });
  const settled = settledPeriods.map(settledPeriod => ({
    from: settledPeriod.from.slice(0, 10),
    to: settledPeriod.to.slice(0, 10),
  }));
  const isYearSettled = (year: number) =>
    subtractIntervals([yearOf(year)], settled).length === 0;
  const thisYear = Number(today.slice(0, 4));
  const lastYear = thisYear - 1;
  const openYears = [
    ...new Set(
      invoices
        .filter(
          invoice =>
            settlementEligible(invoice) && !excludedInvoiceIds.has(invoice.id),
        )
        .flatMap(invoice => {
          const years: number[] = [];
          const last = Number(invoice.periodTo.slice(0, 4));
          for (let y = Number(invoice.periodFrom.slice(0, 4)); y <= last; y++) {
            years.push(y);
          }
          return years;
        }),
    ),
  ]
    .filter(year => year <= thisYear && !isYearSettled(year))
    .sort((a, b) => a - b);
  if (openYears.includes(lastYear)) return yearOf(lastYear);
  if (openYears.length > 0) return yearOf(openYears[0]);
  if (!isYearSettled(lastYear)) return yearOf(lastYear);
  return isYearSettled(thisYear) ? yearOf(lastYear) : yearOf(thisYear);
};

// The settlement's name in the wizard (Lewis, 2026-10-16): what the broker
// typed in "Naam", else the suggestion. Until the broker edits the field
// the draft holds no name, so the suggestion follows the period (and a
// departure's lease once it is known).
export const getDraftSettlementName = (
  draft: Pick<
    SettlementDraftState,
    "name" | "departure" | "period" | "isSupplementary"
  >,
): string => draft.name ?? getSuggestedSettlementName(draft);

// What still blocks the "Periode" step, as i18n keys per field: a name
// (required, Lewis 2026-10-16), a valid period, and a period that does not
// start after today (decision 15, 2026-10-20: its provision months are not
// due yet; rentio-api-next refuses it too).
export const getPeriodStepErrors = (params: {
  name: string;
  period: { from: string; to: string };
  // Today's calendar day (todayIsoDay); without it the check is skipped.
  today?: string;
}): IPeriodStepErrors => {
  const { name, period, today } = params;
  const errors: IPeriodStepErrors = {};
  if (!name.trim()) errors.name = "settlement.period.error.name_required";
  if (!period.from) errors.from = "settlement.period.error.date_required";
  if (!period.to) errors.to = "settlement.period.error.date_required";
  if (period.from && today && period.from > today) {
    errors.from = "settlement.period.error.starts_in_future";
  }
  if (period.from && period.to && period.to < period.from) {
    errors.to = "settlement.period.error.end_before_start";
  }
  return errors;
};

// A period that runs past today counts provision months that are not due
// yet as paid (D7): the step says so, without blocking (decision 15).
export const periodRunsPastToday = (params: {
  period: { from: string; to: string };
  today: string;
}): boolean =>
  !!params.period.to &&
  params.period.to > params.today &&
  !(params.period.from > params.today);

// How far ahead of today a departure's lease may end (M17, decision of
// 2026-10-20): a final settlement is for a tenant who leaves soon. A lease
// that ends later would credit months of provision that are not due yet;
// its notice is registered first, so the settlement runs to the real end.
// rentio-api-next refuses the same on confirm.
export const DEPARTURE_MAX_MONTHS_AHEAD = 6;

// The calendar day `months` after `day` (both "YYYY-MM-DD"), on the last day
// of the month when that month is shorter (31/08 + 6 months = 28/02).
export const addMonthsToDay = (day: string, months: number): string => {
  const [year, month, date] = day.slice(0, 10).split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDate = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(date, lastDate));
  return target.toISOString().slice(0, 10);
};

// Whether a departure's lease ends too far ahead to be settled now (see
// DEPARTURE_MAX_MONTHS_AHEAD): its end is more than six months after today.
export const departureEndsTooFarAhead = (params: {
  leaseEnd: string | undefined;
  today: string;
}): boolean =>
  !!params.leaseEnd &&
  params.leaseEnd.slice(0, 10) >
    addMonthsToDay(params.today, DEPARTURE_MAX_MONTHS_AHEAD);

// The first problem of the "Periode" step, in the order of its fields: the
// reason shown next to the disabled "Volgende".
export const getPeriodStepErrorKey = (
  errors: IPeriodStepErrors,
): string | undefined => errors.name ?? errors.from ?? errors.to;

// A settlement without a single amount: no tenant has a cost or a provision to
// settle and the owner carries nothing. Confirming it would only lock an
// empty statement.
export const isNothingToSettle = (result: {
  perContract: Pick<PerContractResult, "cost" | "provision">[];
  landlordTotal: number;
}): boolean =>
  result.perContract.every(
    line =>
      Math.abs(line.cost) <= TENANT_LINE_THRESHOLD &&
      Math.abs(line.provision) <= TENANT_LINE_THRESHOLD,
  ) && Math.abs(result.landlordTotal) <= OWNER_TOTAL_THRESHOLD;

// The costs a settlement takes from the wizard's list: chosen, eligible
// and not settled elsewhere (or a group's own cost in a unit settlement).
export const chosenCosts = (params: {
  invoices: Invoice[];
  selectedInvoiceIds: Record<string, boolean>;
  excludedInvoiceIds: Set<string>;
}): Invoice[] =>
  params.invoices.filter(
    invoice =>
      params.selectedInvoiceIds[invoice.id] &&
      settlementEligible(invoice) &&
      !params.excludedInvoiceIds.has(invoice.id),
  );

// "Keuze van kosten" goes on with at least one cost chosen, or once the
// broker confirmed that the period had none (Lewis, 2026-10-17): an empty
// choice no longer passes by itself, it refunds every provision.
// A departure of a lease Rentio settled up to its last day already needs no
// such confirmation: its costs were all settled, not absent (Y06).
export const getInvoicesStepErrorKey = (params: {
  chosenCount: number;
  noCostsConfirmed?: boolean;
  isSettledThroughDeparture?: boolean;
}): string | undefined =>
  params.chosenCount > 0 ||
  params.noCostsConfirmed ||
  params.isSettledThroughDeparture
    ? undefined
    : "settlement.invoices.no_costs_required";

// A confirmed settlement without a single cost: its tenants got their whole
// provision back (the costs step's "Er zijn geen kosten voor deze periode").
// Read from its frozen cost list, so no field of its own says it.
export const isWithoutCosts = (snapshot?: { invoiceIds?: string[] }): boolean =>
  !!snapshot && (snapshot.invoiceIds ?? []).length === 0;

// A new settlement only exists in the wizard until its final save, so the
// broker's input is lost when they leave before it. Worth a warning once
// they named it themselves or moved past the first step.
export const hasUnsavedWizardInput = (params: {
  isNewMode: boolean;
  isSaved: boolean;
  draft: SettlementDraftState | null;
}): boolean => {
  const { isNewMode, isSaved, draft } = params;
  if (!isNewMode || isSaved || !draft) return false;
  return draft.name !== undefined || (draft.stepIndex ?? 0) > 0;
};

// What changed in the costs the broker reviewed, once the server refused the
// confirm because they changed meanwhile (another tab paid, billed, edited or
// deleted one): the wizard reloads the costs and shows this on "Keuze van
// kosten".
export interface IReviewedCostsDiff {
  // Gone from the property's costs; as the broker reviewed them.
  removed: Invoice[];
  // No longer up for a settlement, with the reason in the broker's words.
  ineligible: { invoice: Invoice; reason: string }[];
  // Another amount, VAT, period, charge or category; `from` and `to` are the
  // amounts excl. VAT before and now.
  changed: { invoice: Invoice; from: number; to: number }[];
}

const toCents = (amount: number) => Math.round(amount * CENTS_PER_EURO);

const isSamePercentage = (a?: number | string, b?: number | string) =>
  typeof a === "number" && typeof b === "number"
    ? Math.abs(a - b) < PERCENTAGE_TOLERANCE
    : a === b;

// The same figures the API compares on confirm (rentio-api-next
// settlementConfirm.utils findChangedCosts).
const haveFiguresChanged = (before: Invoice, now: Invoice): boolean =>
  toCents(before.amountExclVat) !== toCents(now.amountExclVat) ||
  !isSamePercentage(before.vatPct, now.vatPct) ||
  before.periodFrom !== now.periodFrom ||
  before.periodTo !== now.periodTo ||
  before.target !== now.target ||
  !isSamePercentage(before.tenantSharePct, now.tenantSharePct) ||
  before.category !== now.category;

export const diffReviewedCosts = (params: {
  reviewed: Invoice[];
  current: Invoice[];
}): IReviewedCostsDiff => {
  const { reviewed, current } = params;
  const currentById = new Map(current.map(invoice => [invoice.id, invoice]));
  const diff: IReviewedCostsDiff = { removed: [], ineligible: [], changed: [] };
  for (const before of reviewed) {
    const now = currentById.get(before.id);
    if (!now) {
      diff.removed.push(before);
    } else if (!settlementEligible(now)) {
      diff.ineligible.push({
        invoice: now,
        reason: settlementIneligibleReason(now) ?? "",
      });
    } else if (haveFiguresChanged(before, now)) {
      diff.changed.push({
        invoice: now,
        from: before.amountExclVat,
        to: now.amountExclVat,
      });
    }
  }
  return diff;
};

export const isReviewedCostsDiffEmpty = (diff: IReviewedCostsDiff): boolean =>
  diff.removed.length === 0 &&
  diff.ineligible.length === 0 &&
  diff.changed.length === 0;

// The selection without the costs that are gone from the property: a cost
// that is still there stays selected (the wizard leaves out what can no
// longer be settled by itself), so nothing the broker chose disappears
// because of a list that is reloading.
export const withoutRemovedCosts = (params: {
  selectedInvoiceIds: Record<string, boolean>;
  removed: Invoice[];
}): Record<string, boolean> => {
  const { selectedInvoiceIds, removed } = params;
  if (removed.length === 0) return selectedInvoiceIds;
  const removedIds = new Set(removed.map(invoice => invoice.id));
  return Object.fromEntries(
    Object.entries(selectedInvoiceIds).filter(([id]) => !removedIds.has(id)),
  );
};

// What the primary button of "Verwerking" says. The first click makes the
// settlement final, with payment orders before any payment detail is asked,
// so it says "confirm", never "save"; once saved it only continues to or
// leaves the payment flow. While it runs it says what it is doing.
export const getConfirmPrimaryLabelKey = (params: {
  needsPayments: boolean;
  isSaved: boolean;
  isSaving?: boolean;
}): string => {
  const { needsPayments, isSaved, isSaving } = params;
  if (isSaving) {
    return isSaved ? "settlement.saving" : "settlement.confirming";
  }
  if (needsPayments) {
    return isSaved ? "system.next" : "settlement.confirm.primary_with_orders";
  }
  return isSaved ? "system.close" : "settlement.confirm.primary";
};
